import { useState, type ReactNode } from 'react';
import { line, Size, t, workOrderPartSourcesPath, type PartSourceSite, type WorkOrderPartSourcesAnswer } from '@bms2/shared';
import { Dropdown, NumericInput, RadioGroup, Select } from '../../components/controls';
import { FormFrame } from '../../components/forms';
import { useItemPicker } from '../settings/api';
import { useAddNew } from './AddNewHost';
import { post, useApi } from './api';
import { RefusalText } from './RefusalText';
import styles from './RecordPage.module.css';

/** The radio value Another Site carries; a site id is never this word. */
const ANOTHER = 'another';

/** A part the window opens on: a planned part's Record Fitted fills the item and the quantity. */
export interface PartWindowStart {
  itemId: string;
  itemLabel: string;
  quantity: number;
  unitId: string | null;
}

/** One offer's line: the kind, the site and its on-hand, so an empty van reads 0 before it is picked. */
function offerLabel(kind: string, site: PartSourceSite): string {
  return line(kind, '·', site.siteName, '·', String(site.onHand), t('workOrder.part.onHand'));
}

/**
 * 🔒 The Add Part window (piece 29, P29-16; decision 10320, amending 0530's
 * fixed shelf): which part, how many, and where it came off. Comes off offers
 * the person's van first, the job's shop second, each with its on-hand, and
 * Another Site opens a picker over our own sites. It opens on the site this
 * person last fitted from on this job. A person with no van is told so, and a
 * refused site reads inside the window (decision 3063), which stays open.
 */
export function WorkOrderPartWindow({ workOrderId, materialsPath, apiBaseUrl = '', start, onClose, onAdded }: {
  workOrderId: string;
  materialsPath: string;
  apiBaseUrl?: string;
  start?: PartWindowStart | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const addNew = useAddNew(apiBaseUrl);
  const { itemSearch } = useItemPicker(apiBaseUrl, 'purchase');
  const [item, setItem] = useState<{ id: string; label: string } | null>(start ? { id: start.itemId, label: start.itemLabel } : null);
  const [quantity, setQuantity] = useState<number | null>(start?.quantity ?? 1);
  /** The radio's own answer: a site id, Another Site, or null while the window still opens on the answer's first offer. */
  const [picked, setPicked] = useState<string | null>(null);
  const [otherSiteId, setOtherSiteId] = useState('');
  const [refusal, setRefusal] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);
  const sources = useApi<WorkOrderPartSourcesAnswer>(item ? workOrderPartSourcesPath(workOrderId, item.id) : null, apiBaseUrl);
  const answer = sources.state.kind === 'ready' ? sources.state.data : null;

  const offers = answer ? [answer.van, answer.shop].filter((site): site is PartSourceSite => site !== null) : [];
  // 🔒 The window opens on the remembered site, else the van, else the shop; a remembered site neither of the two is Another Site.
  const first = answer?.firstSiteId ?? null;
  const opening = first === null ? null : offers.some((site) => site.siteId === first) ? first : ANOTHER;
  const choice = picked ?? opening;
  const otherSite = otherSiteId || (picked === null && opening === ANOTHER ? first ?? '' : '');
  const siteId = !answer?.movesStock ? null : choice === ANOTHER ? otherSite || null : choice;

  const pickItem = (id: string, label: string) => {
    setItem({ id, label });
    setPicked(null);
    setOtherSiteId('');
    setRefusal(null);
  };

  const add = async () => {
    if (!item) return;
    setBusy(true);
    const body = { itemId: item.id, quantity, ...(start?.unitId ? { unitId: start.unitId } : {}), ...(siteId ? { siteId } : {}) };
    const outcome = await post(materialsPath, body, apiBaseUrl);
    setBusy(false);
    // 🔒 A customer's site, an empty site or a closed period reads here, with the page the refusal names where it names one (T3511).
    if (!outcome.ok) return setRefusal(<RefusalText message={outcome.message} body={outcome.body} />);
    onAdded();
  };

  const needsSite = answer?.movesStock === true && !siteId;
  return (
    <>
      <FormFrame
        host="window"
        open
        title={t('workOrder.material.add')}
        dataForm="work-order-add-part"
        typed={item !== null && !start}
        onCancel={onClose}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
        act={{
          label: t('workOrder.material.add'),
          disabled: busy || !item || !(quantity ?? 0) || needsSite || answer === null,
          ...(needsSite ? { disabledReason: t('workOrder.part.noSite') } : {}),
          onAct: () => add(),
        }}
      >
        {start ? (
          <p className={styles.hint} data-part-window-item={start.itemId}>{line(t('workOrder.material.item'), '·', start.itemLabel)}</p>
        ) : (
          <Dropdown size={Size.Medium}
            key={item?.id ?? 'none'}
            label={t('workOrder.material.item')}
            entityLabel={t('item.entityItem')}
            search={itemSearch}
            value={item?.label ?? ''}
            onSelect={(row) => pickItem(row.id, row.line1)}
            onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => pickItem(row.id, row.line1))}
            required
          />
        )}
        <NumericInput size={Size.Small} label={t('workOrder.material.quantity')} kind="number" value={quantity} onChange={setQuantity} name="part-window-quantity" />
        {!item ? <p className={styles.hint} data-part-window-pick="true">{t('workOrder.part.pickItem')}</p> : null}
        {sources.state.kind === 'failed' ? <p className={styles.problem} data-part-window-error="true">{sources.state.error}</p> : null}
        {answer && !answer.movesStock ? <p className={styles.hint} data-part-window-service="true">{t('workOrder.part.service')}</p> : null}
        {answer?.movesStock ? (
          <div data-part-window-sources={answer.firstSiteId ?? 'none'}>
            {/* 🔒 The van is drawn even when it holds none, so the person says where the part really came from. */}
            <RadioGroup
              name="part-window-comes-off"
              legend={t('workOrder.material.shelf')}
              value={choice}
              onChange={(value) => { setPicked(value); setRefusal(null); }}
              options={[
                ...(answer.van ? [{ value: answer.van.siteId, label: offerLabel(t('workOrder.part.van'), answer.van) }] : []),
                ...(answer.shop ? [{ value: answer.shop.siteId, label: offerLabel(t('workOrder.part.shop'), answer.shop) }] : []),
                { value: ANOTHER, label: t('workOrder.part.another') },
              ]}
            />
            {answer.van ? null : <p className={styles.hint} data-part-window-no-van="true">{t('workOrder.part.noVan')}</p>}
            {answer.shop ? null : <p className={styles.hint} data-part-window-no-shop="true">{t('workOrder.material.noShelf')}</p>}
            {choice === ANOTHER ? (
              // A site's name and its on-hand together run longer than a Medium field holds, so the picker is Large (decision 0583).
              <Select size={Size.Large}
                label={t('workOrder.part.site')}
                value={otherSite}
                onChange={(value) => { setOtherSiteId(value); setRefusal(null); }}
                placeholder={t('workOrder.new.noneYet')}
                options={answer.sites.map((site) => ({ value: site.siteId, label: line(site.siteName, '·', String(site.onHand), t('workOrder.part.onHand')) }))}
                dataAttribute={{ 'data-part-window-site': 'true' }}
                required
              />
            ) : null}
          </div>
        ) : null}
      </FormFrame>
      {addNew.host}
    </>
  );
}
